/**
 * Recognises visits that come from an AI assistant (ChatGPT, Perplexity,
 * Claude, Gemini, Copilot, ...), so the analytics can show how many visitors
 * and demo requests AI answers send, next to Google and direct traffic.
 *
 * Two signals: the referrer's host (assistants that open links in a normal tab
 * pass it) and utm_source (ChatGPT search tags its links utm_source=chatgpt.com;
 * Perplexity and Copilot sometimes tag theirs too). Many assistant clicks
 * arrive with neither (mobile apps, copied links) and count as direct.
 */

/** Host suffix or utm_source value -> assistant name used in analytics. */
const ASSISTANTS: ReadonlyArray<[RegExp, string]> = [
  [/(^|\.)(chatgpt\.com|chat\.openai\.com|openai\.com)$/, 'chatgpt'],
  [/(^|\.)perplexity\.ai$/, 'perplexity'],
  [/(^|\.)claude\.ai$/, 'claude'],
  [/(^|\.)(gemini\.google\.com|bard\.google\.com)$/, 'gemini'],
  [/(^|\.)(copilot\.microsoft\.com|copilot\.cloud\.microsoft)$/, 'copilot'],
  [/(^|\.)(chat\.)?deepseek\.com$/, 'deepseek'],
  [/(^|\.)(grok\.com|x\.ai)$/, 'grok'],
  [/(^|\.)meta\.ai$/, 'meta-ai'],
  [/(^|\.)chat\.mistral\.ai$/, 'mistral'],
  [/(^|\.)you\.com$/, 'you'],
  [/(^|\.)poe\.com$/, 'poe'],
];

/** utm_source values assistants use that are not host names. */
const UTM_NAMES: Record<string, string> = {
  chatgpt: 'chatgpt',
  openai: 'chatgpt',
  perplexity: 'perplexity',
  claude: 'claude',
  gemini: 'gemini',
  copilot: 'copilot',
};

const matchHost = (host: string): string => {
  const clean = host.toLowerCase().replace(/^www\./, '');
  const hit = ASSISTANTS.find(([pattern]) => pattern.test(clean));
  return hit ? hit[1] : '';
};

/**
 * The assistant a visit came from ("chatgpt", "perplexity", ...), or "" for
 * any other visit. utm_source wins over the referrer when both are present.
 */
export function aiAssistantSource(referrer: string, utmSource: string): string {
  const utm = utmSource.trim().toLowerCase();
  if (utm) {
    const fromUtm = UTM_NAMES[utm] || matchHost(utm);
    if (fromUtm) return fromUtm;
  }
  if (!referrer) return '';
  try {
    return matchHost(new URL(referrer).hostname);
  } catch {
    return '';
  }
}
